import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Table } from "react-super-responsive-table";
import { Loader2, Printer, X } from "lucide-react";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";

const TEXT_FILTERS_DEFAULT = {
  kodVZP: "",
  pdk: "",
  nazev: "",
};

const RANGE_FILTERS_DEFAULT = {
  mnozstvi: { mode: "", value1: "", value2: "" },
  exspirace: { mode: "", value1: "", value2: "" },
  zbyvaDni: { mode: "", value1: "", value2: "" },
};

function parseCzDateToDate(value) {
  if (!value) return null;

  if (value instanceof Date && !isNaN(value.getTime())) return value;

  const str = String(value).trim();

  // formát z backendu: "D. M. YYYY"
  const match = str.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (match) {
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const d = new Date(year, month - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }

  const fallback = new Date(str);
  return isNaN(fallback.getTime()) ? null : fallback;
}

function formatDateForInput(value) {
  const d = parseCzDateToDate(value);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(String(value).replace(",", ".").trim());
  return Number.isNaN(num) ? null : num;
}

function compareByMode(itemValue, mode, value1, value2) {
  if (!mode) return true;
  if (itemValue === null || itemValue === undefined) return false;

  if (mode === "gt") return itemValue > value1;
  if (mode === "gte") return itemValue >= value1;
  if (mode === "lt") return itemValue < value1;
  if (mode === "lte") return itemValue <= value1;
  if (mode === "between") {
    if (value1 === null || value2 === null) return true;
    const min = Math.min(value1, value2);
    const max = Math.max(value1, value2);
    return itemValue >= min && itemValue <= max;
  }

  return true;
}

export default function OhrozenciPage() {
  const { user } = useAuth();

  const [data, setData] = useState([]);
  const [textFilters, setTextFilters] = useState(TEXT_FILTERS_DEFAULT);
  const [rangeFilters, setRangeFilters] = useState(RANGE_FILTERS_DEFAULT);
  const [sortField, setSortField] = useState("zbyvaDni");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!user || !user.pharmacyCode) {
      setLoading(false);
      return;
    }

    const firma = user.firma || "LEMON";
    const pc = String(user.pharmacyCode).replace(/^0+/, "");

    const fetchData = async () => {
      try {
        const response = await fetch(`/api/ohrozenci/${firma}/${pc}`);
        if (!response.ok) {
          console.error("Chyba při načítání ohroženců:", response.statusText);
          setLoading(false);
          return;
        }

        const json = await response.json();

        const active = (json.ohrozenci || []).filter(
          (x) => !x.stav || !["uzavřeno", "odesláno", "přijato"].includes(x.stav)
        );

        setData(active);
        setLastUpdated(json.lastBatch || null);
      } catch (err) {
        console.error("Nepodařilo se načíst ohrožence:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    // textové filtry
    result = result.filter((item) => {
      const kodVZPMatch = String(item.kodVZP || "")
        .toLowerCase()
        .includes(textFilters.kodVZP.trim().toLowerCase());

      const pdkMatch = String(item.pdk || "")
        .toLowerCase()
        .includes(textFilters.pdk.trim().toLowerCase());

      const nazevMatch = String(item.nazev || "")
        .toLowerCase()
        .includes(textFilters.nazev.trim().toLowerCase());

      return kodVZPMatch && pdkMatch && nazevMatch;
    });

    // MNOŽSTVÍ
    result = result.filter((item) => {
      const filter = rangeFilters.mnozstvi;
      if (!filter.mode) return true;

      const itemValue = normalizeNumber(item.mnozstvi);
      const value1 = normalizeNumber(filter.value1);
      const value2 = normalizeNumber(filter.value2);

      if (filter.mode === "between" && (value1 === null || value2 === null)) return true;
      if (filter.mode !== "between" && value1 === null) return true;

      return compareByMode(itemValue, filter.mode, value1, value2);
    });

    // ZBÝVÁ DNÍ
    result = result.filter((item) => {
      const filter = rangeFilters.zbyvaDni;
      if (!filter.mode) return true;

      const itemValue = normalizeNumber(item.zbyvaDni);
      const value1 = normalizeNumber(filter.value1);
      const value2 = normalizeNumber(filter.value2);

      if (filter.mode === "between" && (value1 === null || value2 === null)) return true;
      if (filter.mode !== "between" && value1 === null) return true;

      return compareByMode(itemValue, filter.mode, value1, value2);
    });

    // EXSPIRACE
    result = result.filter((item) => {
      const filter = rangeFilters.exspirace;
      if (!filter.mode) return true;

      const itemDate = parseCzDateToDate(item.exspirace);
      if (!itemDate) return false;

      const itemValue = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      ).getTime();

      const date1 = filter.value1 ? new Date(filter.value1) : null;
      const date2 = filter.value2 ? new Date(filter.value2) : null;

      const value1 =
        date1 && !isNaN(date1.getTime())
          ? new Date(date1.getFullYear(), date1.getMonth(), date1.getDate()).getTime()
          : null;

      const value2 =
        date2 && !isNaN(date2.getTime())
          ? new Date(date2.getFullYear(), date2.getMonth(), date2.getDate()).getTime()
          : null;

      if (filter.mode === "between" && (value1 === null || value2 === null)) return true;
      if (filter.mode !== "between" && value1 === null) return true;

      return compareByMode(itemValue, filter.mode, value1, value2);
    });

    // řazení
    result.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];

      if (sortField === "mnozstvi" || sortField === "zbyvaDni") {
        const aNum = normalizeNumber(aVal) ?? 0;
        const bNum = normalizeNumber(bVal) ?? 0;
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (sortField === "exspirace") {
        const aDate = parseCzDateToDate(aVal)?.getTime() ?? 0;
        const bDate = parseCzDateToDate(bVal)?.getTime() ?? 0;
        return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
      }

      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();

      return sortDirection === "asc"
        ? aStr.localeCompare(bStr, "cs")
        : bStr.localeCompare(aStr, "cs");
    });

    return result;
  }, [data, textFilters, rangeFilters, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const handleTextFilterChange = (key, value) => {
    setTextFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRangeFilterChange = (key, field, value) => {
    setRangeFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
  };

  const clearFilters = () => {
    setTextFilters(TEXT_FILTERS_DEFAULT);
    setRangeFilters(RANGE_FILTERS_DEFAULT);
    setSortField("zbyvaDni");
    setSortDirection("asc");
  };

  const handlePrint = () => {
    window.print();
  };

  const renderRangeFilter = (key, label, type = "number") => {
    const filter = rangeFilters[key];
    const isBetween = filter.mode === "between";

    return (
      <div className="border rounded-xl p-3 bg-white shadow-sm">
        <div className="text-xs font-semibold text-gray-600 mb-2">{label}</div>

        <select
          value={filter.mode}
          onChange={(e) => handleRangeFilterChange(key, "mode", e.target.value)}
          className="border p-2 rounded w-full mb-2"
        >
          <option value="">Bez filtru</option>
          <option value="gt">Větší než</option>
          <option value="gte">Větší nebo rovno</option>
          <option value="lt">Menší než</option>
          <option value="lte">Menší nebo rovno</option>
          <option value="between">Interval</option>
        </select>

        <div className="flex gap-2">
          <input
            type={type}
            value={filter.value1}
            onChange={(e) => handleRangeFilterChange(key, "value1", e.target.value)}
            placeholder={isBetween ? "Od" : "Hodnota"}
            className="border p-2 rounded w-full"
          />

          {isBetween && (
            <input
              type={type}
              value={filter.value2}
              onChange={(e) => handleRangeFilterChange(key, "value2", e.target.value)}
              placeholder="Do"
              className="border p-2 rounded w-full"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="ohrozenci-print-root" className="p-4 print:p-0">
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm;
          }

          body * {
            visibility: hidden !important;
          }

          #ohrozenci-print-root,
          #ohrozenci-print-root * {
            visibility: visible !important;
          }

          #ohrozenci-print-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .print-only {
            display: block !important;
          }

          .print-table-wrap {
            overflow: visible !important;
          }

          .custom-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 11px !important;
            table-layout: fixed !important;
          }

          .custom-table th,
          .custom-table td {
            border: 1px solid #666 !important;
            padding: 6px 8px !important;
            color: #000 !important;
            background: #fff !important;
          }

          /* KÓD VZP */
          .custom-table th:nth-child(1),
          .custom-table td:nth-child(1) {
            width: 10% !important;
            white-space: nowrap !important;
          }

          /* PDK */
          .custom-table th:nth-child(2),
          .custom-table td:nth-child(2) {
            width: 15% !important;
            white-space: nowrap !important;
          }

          /* zarovnání číselných sloupců vpravo */
          .custom-table th:nth-child(4),
          .custom-table td:nth-child(4),
          .custom-table th:nth-child(5),
          .custom-table td:nth-child(5),
          .custom-table th:nth-child(6),
          .custom-table td:nth-child(6) {
            text-align: right !important;
          }

          /* NÁZEV */
          .custom-table th:nth-child(3),
          .custom-table td:nth-child(3) {
            width: 38% !important;
            font-size: 10px !important;
            line-height: 1.15 !important;
            word-break: break-word !important;
          }

          /* MNOŽSTVÍ */
          .custom-table th:nth-child(4),
          .custom-table td:nth-child(4) {
            width: 12% !important;
            white-space: nowrap !important;
          }

          /* EXSPIRACE */
          .custom-table th:nth-child(5),
          .custom-table td:nth-child(5) {
            width: 14% !important;
            white-space: nowrap !important;
          }

          /* ZBÝVÁ DNÍ */
          .custom-table th:nth-child(6),
          .custom-table td:nth-child(6) {
            width: 11% !important;
            white-space: nowrap !important;
          }          
          /* úprava šířek sloupců pro tisk */
          .custom-table th:nth-child(3),
          .custom-table td:nth-child(3) {
            font-size: 10px !important;
          }

          /* sloupec EXSPIRACE */
          .custom-table th:nth-child(5),
          .custom-table td:nth-child(5) {
            width: 95px !important;
            white-space: nowrap !important;
          }
          .custom-table th {
            font-weight: 700 !important;
          }
        }

        @media screen {
          .print-only {
            display: none !important;
          }
        }
      `}</style>

      <div className="flex flex-col gap-3 mb-4 no-print">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-2xl font-semibold">Ohrožené přípravky</h2>

            {lastUpdated ? (
              <span className="text-sm text-gray-500">
                Poslední export:{" "}
                {new Date(lastUpdated)
                  .toLocaleString("cs-CZ", {
                    day: "numeric",
                    month: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                  .replace(/\. /g, ".")}
              </span>
            ) : !loading ? (
              <span className="text-sm text-rose-600">
                Zatím nebyl nahrán žádný export
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-gray-50 text-sm"
            >
              <X className="w-4 h-4" />
              Vymazat filtry
            </button>

            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
            >
              <Printer className="w-4 h-4" />
              Tisk
            </button>
          </div>
        </div>

        {/* horní textové filtry */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">KÓD VZP</div>
            <input
              type="text"
              value={textFilters.kodVZP}
              onChange={(e) => handleTextFilterChange("kodVZP", e.target.value)}
              placeholder="Filtrovat podle KÓDU VZP"
              className="border p-2 rounded w-full"
            />
          </div>

          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">PDK</div>
            <input
              type="text"
              value={textFilters.pdk}
              onChange={(e) => handleTextFilterChange("pdk", e.target.value)}
              placeholder="Filtrovat podle PDK"
              className="border p-2 rounded w-full"
            />
          </div>

          <div className="border rounded-xl p-3 bg-white shadow-sm">
            <div className="text-xs font-semibold text-gray-600 mb-2">NÁZEV</div>
            <input
              type="text"
              value={textFilters.nazev}
              onChange={(e) => handleTextFilterChange("nazev", e.target.value)}
              placeholder="Filtrovat podle názvu"
              className="border p-2 rounded w-full"
            />
          </div>
        </div>

        {/* spodní pokročilé filtry */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {renderRangeFilter("mnozstvi", "MNOŽSTVÍ", "number")}
          {renderRangeFilter("exspirace", "EXSPIRACE", "date")}
          {renderRangeFilter("zbyvaDni", "ZBÝVÁ DNÍ", "number")}
        </div>

        <div className="text-sm text-gray-500">
          Zobrazeno záznamů: {filteredAndSortedData.length} / {data.length}
        </div>
      </div>

      <div className="print-only mb-4">
        <h1 className="text-xl font-bold">Ohrožené přípravky</h1>
        <div className="text-sm mt-1">
          Pobočka: {String(user?.pharmacyCode || "").replace(/^0+/, "") || "-"}
        </div>
        <div className="text-sm">
          Vytištěno:{" "}
          {new Date().toLocaleString("cs-CZ", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        {lastUpdated ? (
          <div className="text-sm">
            Poslední export:{" "}
            {new Date(lastUpdated)
              .toLocaleString("cs-CZ", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
              .replace(/\. /g, ".")}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Načítám data...
        </div>
      ) : filteredAndSortedData.length === 0 ? (
        <p>Nenalezeny žádné ohrožené přípravky.</p>
      ) : (
        <div className="print-table-wrap overflow-x-auto">
          <Table className="custom-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("kodVZP")} className="cursor-pointer select-none">
                  KÓD VZP{sortField === "kodVZP" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("pdk")} className="cursor-pointer select-none">
                  PDK{sortField === "pdk" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("nazev")} className="cursor-pointer select-none">
                  NÁZEV{sortField === "nazev" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("mnozstvi")} className="cursor-pointer select-none">
                  MNOŽSTVÍ{sortField === "mnozstvi" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("exspirace")} className="cursor-pointer select-none">
                  EXSPIRACE{sortField === "exspirace" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
                <th onClick={() => handleSort("zbyvaDni")} className="cursor-pointer select-none">
                  ZBÝVÁ DNÍ{sortField === "zbyvaDni" ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredAndSortedData.map((item, index) => (
                <tr key={`${item.kodVZP || item.pdk || item.nazev || "row"}-${index}`}>
                  <td>{item.kodVZP || ""}</td>
                  <td>{item.pdk || ""}</td>
                  <td>{item.nazev || ""}</td>
                  <td>{item.mnozstvi ?? ""}</td>
                  <td>{item.exspirace || ""}</td>
                  <td>{item.zbyvaDni ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}